import { Injectable, Scopes } from '@visiba/instantiation';
import { HttpService } from '@application/http.service';
import { ApiBaseClient } from './_api_base_client';
import type {
	BookSlotApiInput,
	ClientAppointmentApiModel,
	ClientTimeslotApiModel,
	LocalityType,
	SearchCapabilitiesApiModel,
	TimeslotAvailabilityCountsApiModel,
	TimeslotAvailabilityGroupingApiModel,
	TimeslotLockApiModel,
} from './generated/clientv2/models';

@Injectable({
	scope: Scopes.Transient,
})
export class TimeSlotApiService extends ApiBaseClient {
	constructor(private readonly httpService: HttpService) {
		super('units');
	}

	public async getSearchCapabilities(unitId: number) {
		return this.httpService.get<SearchCapabilitiesApiModel>(`${this.baseUri}/${unitId}/timeslots/searchcapabilities`);
	}

	public getTimeSlots(
		unitId: number,
		options: {
			searchContextId?: string;
			fromDate: string;
			toDate: string;
			fromTime: string;
			toTime: string;
			localityType: LocalityType;
			conditionId?: number;
			specialityId?: number;
			filterableUnitId?: number;
			resourceId?: number;
			physicalLocationId?: number;
		},
	) {
		const url = this.httpService.createUrl(`${this.baseUri}/${unitId}/timeslots`);
		url.searchParams.append('localityType', options.localityType);
		url.searchParams.append('fromDate', options.fromDate);
		url.searchParams.append('toDate', options.toDate);
		url.searchParams.append('fromTime', options.fromTime);
		url.searchParams.append('toTime', options.toTime);

		if (options?.searchContextId) url.searchParams.append('searchContextId', options.searchContextId);
		if (options?.conditionId) url.searchParams.append('conditionId', options.conditionId.toString());
		if (options?.specialityId) url.searchParams.append('specialityId', options.specialityId.toString());
		if (options?.resourceId) url.searchParams.append('resourceId', options.resourceId.toString());
		if (options?.filterableUnitId) url.searchParams.append('filterableUnitId', options.filterableUnitId.toString());
		if (options?.physicalLocationId) url.searchParams.append('physicalLocationId', options.physicalLocationId.toString());

		return this.httpService.get<ClientTimeslotApiModel[]>(url);
	}

	public getTimeSlot(unitId: number, timeSlotId: string) {
		return this.httpService.get<ClientTimeslotApiModel>(`${this.baseUri}/${unitId}/timeslots/${timeSlotId}`);
	}

	public bookTimeSlot(unitId: number, timeSlotId: string, bookingData: BookSlotApiInput) {
		return this.httpService.post<ClientAppointmentApiModel>(
			`${this.baseUri}/${unitId}/timeslots/${timeSlotId}/book`,
			bookingData as unknown as Record<string, unknown>,
		);
	}

	public availableTimeSlots(
		unitId: number,
		options?: {
			searchContextId?: string;
			specialityId?: number;
			conditionId?: number;
			resourceId?: number;
			filterableUnitId?: number;
			fromDate?: string;
			toDate?: string;
		},
	) {
		const url = this.httpService.createUrl(`${this.baseUri}/${unitId}/timeslots/availability`);

		if (options?.specialityId) url.searchParams.append('specialityId', options.specialityId.toString());
		if (options?.conditionId) url.searchParams.append('conditionId', options.conditionId.toString());
		if (options?.resourceId) url.searchParams.append('resourceId', options.resourceId.toString());
		if (options?.filterableUnitId) url.searchParams.append('filterableUnitId', options.filterableUnitId.toString());
		if (options?.fromDate) url.searchParams.append('fromDate', options.fromDate);
		if (options?.toDate) url.searchParams.append('toDate', options.toDate);
		if (options?.searchContextId) url.searchParams.append('searchContextId', options.searchContextId);

		return this.httpService.get<TimeslotAvailabilityGroupingApiModel>(url);
	}

	public availabilityCounts(
		unitId: number,
		options?: {
			searchContextId?: string;
			specialityId?: number;
			conditionId?: number;
			resourceId?: number;
			filterableUnitId?: number;
			physicalLocationId?: number;
			fromDate?: string;
			toDate?: string;
			fromTime?: string;
			toTime?: string;
			localityType?: string;
		},
	) {
		const url = this.httpService.createUrl(`${this.baseUri}/${unitId}/timeslots/availabilityCounts`);

		if (options?.searchContextId) url.searchParams.append('searchContextId', options.searchContextId);
		if (options?.fromDate) url.searchParams.append('fromDate', options.fromDate);
		if (options?.toDate) url.searchParams.append('toDate', options.toDate);
		if (options?.fromTime) url.searchParams.append('fromTime', options.fromTime);
		if (options?.toTime) url.searchParams.append('toTime', options.toTime);
		if (options?.specialityId) url.searchParams.append('specialityId', options.specialityId.toString());
		if (options?.conditionId) url.searchParams.append('conditionId', options.conditionId.toString());
		if (options?.resourceId) url.searchParams.append('resourceId', options.resourceId.toString());
		if (options?.filterableUnitId) url.searchParams.append('filterableUnitId', options.filterableUnitId.toString());
		if (options?.physicalLocationId) url.searchParams.append('physicalLocationId', options.physicalLocationId.toString());
		if (options?.localityType) url.searchParams.append('localityType', options.localityType);

		return this.httpService.get<TimeslotAvailabilityCountsApiModel>(url);
	}

	public lockTimeSlot(unitId: number, timeSlotId: string) {
		return this.httpService.post<TimeslotLockApiModel>(`${this.baseUri}/${unitId}/timeslots/${timeSlotId}/locks`, {});
	}

	public updateLockedTimeSlot(unitId: number, timeSlotLockId: string) {
		return this.httpService.put<TimeslotLockApiModel>(`${this.baseUri}/${unitId}/timeslotlocks/${timeSlotLockId}`, {});
	}

	public deleteLockTimeSlot(unitId: number, timeSlotLockId: string) {
		return this.httpService.delete<void>(`${this.baseUri}/${unitId}/timeslotlocks/${timeSlotLockId}`);
	}
}
